import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { useHealthCheck } from "@/hooks/useHealthCheck";
import { WorkspaceProvider } from "@/context/WorkspaceContext";
import { CustomizationProvider } from "@/context/CustomizationContext";
import { ThemeProvider } from "@/context/ThemeContext";
import AuthCallback from "@/components/AuthCallback";
import LoginPage from "@/pages/LoginPage";
import LandingPage from "@/pages/LandingPage";
import AppShell from "@/components/layout/AppShell";
import DashboardPage from "@/pages/DashboardPage";
import ReportBuilderPage from "@/pages/ReportBuilderPage";
import RevenueSummaryPage from "@/pages/RevenueSummaryPage";
import RevenueDetailsPage from "@/pages/RevenueDetailsPage";
import BusinessTrackerPage from "@/pages/BusinessTrackerPage";
import ServicesPage from "@/pages/ServicesPage";
import SettingsPage from "@/pages/SettingsPage";
import ControlPanelPage from "@/pages/ControlPanelPage";
import AuditTrailPage from "@/pages/AuditTrailPage";
import { permissionsFor } from "@/lib/permissions";
import { Loader2 } from "lucide-react";

const FullPageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--primary))]" />
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!permissionsFor(user.role).canManage) return <Navigate to="/dashboard" replace />;
  return children;
};

const ControlPanelRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!permissionsFor(user.role).canCustomize) return <Navigate to="/dashboard" replace />;
  return children;
};

const SuperAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "super_admin") return <Navigate to="/dashboard" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

/** Mounts the health-check polling. Only polls while a user session is active. */
function ConnectionMonitor() {
  const { user } = useAuth();
  useHealthCheck({ enabled: !!user });
  return null;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/report-builder" element={<ReportBuilderPage />} />
        <Route path="/revenue-summary" element={<RevenueSummaryPage />} />
        <Route path="/revenue-details" element={<RevenueDetailsPage />} />
        <Route path="/business-tracker" element={<BusinessTrackerPage />} />
        <Route path="/services" element={<AdminRoute><ServicesPage /></AdminRoute>} />
        <Route path="/control-panel" element={<ControlPanelRoute><ControlPanelPage /></ControlPanelRoute>} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/audit" element={<SuperAdminRoute><AuditTrailPage /></SuperAdminRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ThemeProvider>
          <WorkspaceProvider>
            <CustomizationProvider>
              <BrowserRouter>
                <AppRouter />
              </BrowserRouter>
            </CustomizationProvider>
          </WorkspaceProvider>
        </ThemeProvider>
        <ConnectionMonitor />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
